'use client';

import { useRef, useMemo } from 'react';
import { Squares2X2Icon, ListBulletIcon } from '@heroicons/react/24/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import Button from '@/components/Button';
import { DemoCard } from './DemoCard';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { clsx } from 'clsx';
import OpenSourceCard from './OpenSourceCard';
import { useDemoLayout } from '../../contexts/demoLayoutContext';
import { StaticImageData } from 'next/image';

type CardData = {
  title: string;
  desc: string;
  link: string;
  keywords: string[];
  cardImages: {
    row: StaticImageData;
    grid: StaticImageData;
  };
};

interface AttributePageProps {
  heading: string;
  subHeading: string;
  cardData: CardData[];
  Icon: React.ReactNode;
  forwardLink?: {
    href: string;
    text: string;
  };
  backLink?: {
    href: string;
    text: string;
  };
}

export function AttributePage({ heading, subHeading, cardData, forwardLink, backLink, Icon }: AttributePageProps) {
  const containerRef = useRef(null);
  const { demoLayout, setDemoLayout } = useDemoLayout();

  useGSAP(
    () => {
      if (document && !document.querySelector('.demo-card-gsap')) return;
      gsap.fromTo(
        '.demo-card-gsap',
        {
          autoAlpha: 0,
          y: 20,
        },
        { y: 0, autoAlpha: 1, stagger: { each: 0.05 }, duration: 0.25 },
      );
    },
    { dependencies: [demoLayout], scope: containerRef },
  );

  const filterCards = useMemo(() => {
    return cardData.map((item, idx) => (
      <DemoCard key={item.title.slice(0, 5) + idx} {...item} Icon={Icon} layoutType={demoLayout} />
    ));
  }, [cardData, demoLayout, Icon]);

  return (
    <div ref={containerRef} className="min-h-screen-with-nav flex flex-col px-4 py-6 md:px-12 lg:pl-6 lg:pr-3">
      <div className="mb-6 grid grid-cols-1 gap-4 md:mb-8 md:grid-cols-[auto_1fr] md:pt-6">
        <div className="grid size-10 place-items-center self-center rounded-lg bg-dark-background p-2 text-gradle-blue">
          {Icon}
        </div>
        <h1 className="text-h3">{heading}</h1>
        <div className="text-body text-gray-3 md:col-start-2">{subHeading}</div>
      </div>
      <section>
        <div className="mb-6 hidden items-center justify-end gap-2 md:flex">
          <button onClick={() => setDemoLayout('row')} className="rounded p-1 transition-colors hover:bg-gray-5">
            <ListBulletIcon
              className={clsx(
                'size-6 cursor-pointer transition-colors',
                demoLayout == 'row' ? 'text-gradle-blue' : 'text-gray-3',
              )}
            />
          </button>
          <button onClick={() => setDemoLayout('grid')} className="rounded p-1 transition-colors hover:bg-gray-5">
            <Squares2X2Icon
              className={clsx(
                'size-6 cursor-pointer transition-colors',
                demoLayout == 'grid' ? 'text-gradle-blue' : 'text-gray-3',
              )}
            />
          </button>
        </div>
        <div
          className={clsx(
            'mb-12 grid grid-cols-1 grid-rows-[1fr] gap-y-6 md:gap-x-4 md:gap-y-4 lg:gap-x-6',
            demoLayout == 'grid' ? 'md:grid-cols-2' : 'md:grid-cols-1',
          )}
        >
          {filterCards}
        </div>
        <div className="mb-12 border-y border-solid border-gray-5 md:mb-16">
          <div className="mb-6 mt-6 flex justify-between">
            {backLink && (
              <Button variant="ghost-dark" size="lg" className="group gap-2" href={backLink.href}>
                <ChevronLeftIcon className="size-5 text-gray-1 group-hover:text-white" /> {backLink.text}
              </Button>
            )}
            {forwardLink && (
              <Button variant="ghost-dark" size="lg" className="group gap-2" href={forwardLink.href}>
                {forwardLink.text} <ChevronRightIcon className="size-5 text-gray-1 group-hover:text-white" />
              </Button>
            )}
          </div>
        </div>
        <OpenSourceCard />
      </section>
    </div>
  );
}
